import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Database,
  Download,
  Upload,
  Trash2,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileJson,
  FileSpreadsheet,
  HardDrive,
  Loader2,
  Settings,
  Calendar,
  Shield
} from 'lucide-react';

const DatabaseBackupPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [collections, setCollections] = useState([]);
  const [settings, setSettings] = useState({
    enabled: true,
    schedule_time: '02:00',
    retention_days: 30,
    include_all_collections: true
  });
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [exporting, setExporting] = useState({});

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.backups || []);
      setCollections(response.data.collections || []);
      setSettings(response.data.settings || settings);
    } catch (error) {
      toast.error('Failed to load backup data');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async (type = 'manual') => {
    try {
      setCreatingBackup(true);
      const response = await axios.post(
        `${API}/backups/create?backup_type=${type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Backup created successfully!');
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const downloadBackup = async (backup) => {
    try {
      const response = await axios.get(`${API}/backups/download/${backup.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const confirmRestore = (backup) => {
    setSelectedBackup(backup);
    setRestoreDialogOpen(true);
  };

  const restoreBackup = async () => {
    if (!selectedBackup) return;
    
    try {
      setRestoring(true);
      await axios.post(
        `${API}/backups/restore/${selectedBackup.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Database restored successfully!');
      setRestoreDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  const deleteBackup = async (backup) => {
    if (!confirm('Are you sure you want to delete this backup?')) return;
    
    try {
      await axios.delete(`${API}/backups/${backup.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup deleted');
      fetchBackups();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const updateSettings = async () => {
    try {
      await axios.put(`${API}/backups/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const exportCollection = async (collectionName, format) => {
    try {
      setExporting({ ...exporting, [collectionName]: format });
      
      const response = await axios.get(
        `${API}/backups/export/${collectionName}?format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${collectionName}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${collectionName} exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export collection');
    } finally {
      setExporting({ ...exporting, [collectionName]: null });
    }
  };

  const downloadFullBackup = async () => {
    try {
      const response = await axios.get(`${API}/backups/full-export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `spt_full_export_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Full backup downloaded');
    } catch (error) {
      toast.error('Failed to download full backup');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (kb) => {
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F3C4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Database className="w-7 h-7 text-[#1F3C4F]" />
              Database Backup
            </h1>
            <p className="text-slate-500 mt-1">Manage backups, exports, and data recovery</p>
          </div>
          <Button onClick={fetchBackups} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Pre-Deployment Warning */}
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900">Before Deploying to Production</h3>
                <p className="text-orange-700 text-sm mt-1">
                  Always create a backup before deploying new changes to protect your data.
                </p>
              </div>
              <Button 
                onClick={() => createBackup('pre-deploy')}
                disabled={creatingBackup}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {creatingBackup ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                Backup Now Before Deploying
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Full Data Backup */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="w-5 h-5" />
                Full Data Backup
              </CardTitle>
              <CardDescription>
                Download a complete backup of all your data in JSON format
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={downloadFullBackup} className="w-full bg-[#1F3C4F] hover:bg-[#152a38]">
                <Download className="w-4 h-4 mr-2" />
                Download Full Backup (JSON)
              </Button>
            </CardContent>
          </Card>

          {/* Quick Backup */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Create Cloud Backup
              </CardTitle>
              <CardDescription>
                Save a backup to cloud storage (Cloudinary) for safekeeping
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => createBackup('manual')}
                disabled={creatingBackup}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {creatingBackup ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Backup Now (to Cloudinary)
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Automatic Backup Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Automatic Daily Backups
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <Switch 
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({...settings, enabled: checked})}
                  id="auto-backup"
                />
                <Label htmlFor="auto-backup" className="font-medium">
                  {settings.enabled ? (
                    <span className="text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Enabled
                    </span>
                  ) : (
                    <span className="text-slate-500">Disabled</span>
                  )}
                </Label>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Schedule</Label>
                <Select 
                  value={settings.schedule_time}
                  onValueChange={(value) => setSettings({...settings, schedule_time: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="00:00">12:00 AM</SelectItem>
                    <SelectItem value="02:00">2:00 AM</SelectItem>
                    <SelectItem value="04:00">4:00 AM</SelectItem>
                    <SelectItem value="06:00">6:00 AM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Retention</Label>
                <Select 
                  value={String(settings.retention_days)}
                  onValueChange={(value) => setSettings({...settings, retention_days: parseInt(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={updateSettings} variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                Save Settings
              </Button>
            </div>
            
            {/* Recent Backups */}
            {backups.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-slate-700 mb-3">Recent Backups</h4>
                <div className="space-y-2">
                  {backups.map((backup) => (
                    <div 
                      key={backup.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <FileJson className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-sm text-slate-900">{backup.filename}</p>
                          <p className="text-xs text-slate-500">
                            {formatSize(backup.size_kb)} • {backup.record_count} records • {formatDate(backup.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          backup.type === 'pre-deploy' 
                            ? 'bg-orange-100 text-orange-700'
                            : backup.type === 'automatic'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {backup.type}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadBackup(backup)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() => confirmRestore(backup)}
                        >
                          Restore
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteBackup(backup)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Individual Collection Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Export Individual Collections
            </CardTitle>
            <CardDescription>
              Export specific data tables in JSON or CSV format
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {collections.map((collection) => (
                <div 
                  key={collection.name}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                >
                  <div>
                    <p className="font-medium text-sm text-slate-900 capitalize">
                      {collection.name.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {collection.record_count} records
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={exporting[collection.name] === 'json'}
                      onClick={() => exportCollection(collection.name, 'json')}
                    >
                      {exporting[collection.name] === 'json' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        'JSON'
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={exporting[collection.name] === 'csv'}
                      onClick={() => exportCollection(collection.name, 'csv')}
                    >
                      {exporting[collection.name] === 'csv' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        'CSV'
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Restore Confirmation Dialog */}
        <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="w-5 h-5" />
                Confirm Database Restore
              </DialogTitle>
              <DialogDescription className="pt-2">
                <p className="text-red-600 font-medium mb-2">
                  Warning: This action will overwrite all current data!
                </p>
                <p>
                  Restoring from backup <strong>{selectedBackup?.filename}</strong> will replace 
                  all existing data in your database. This action cannot be undone.
                </p>
                <p className="mt-2">
                  We recommend creating a new backup before proceeding.
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={restoreBackup}
                disabled={restoring}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {restoring ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Yes, Restore Database
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DatabaseBackupPage;
